export interface StrategicProject {
  id: string;
  name: string;
  objective: string;
  currentState: {
    score: number; // 0-100
    status: 'critical' | 'warning' | 'good';
    analysis: string;
    findings: string[];
  };
  opportunities: {
    potential: string;
    estimatedImpact: string;
    timeline: string;
  };
  recommendations: {
    quick_wins: string[];
    strategic_actions: string[];
    investment_needed: string;
  };
  kpis: {
    name: string;
    current: string | number;
    target: string | number;
    gap: string;
  }[];
}

// Analyse stratégique pour Le 39V basée sur les données réelles
export const strategicProjects39V: StrategicProject[] = [
  {
    id: 'international_seo',
    name: 'Ouverture à la Clientèle Internationale',
    objective: 'Capter les touristes anglophones, hispanophones et lusophones recherchant une expérience gastronomique à Paris',
    currentState: {
      score: 15,
      status: 'critical',
      analysis: '🚨 SITUATION CRITIQUE : Quasi-absence totale sur les recherches internationales. Sur 49 mots-clés positionnés, AUCUN ne cible les touristes étrangers. Le site reçoit seulement 7% de trafic US et 0% ES/PT. Perte estimée de 80% du potentiel de clientèle internationale.',
      findings: [
        '❌ Aucune balise hreflang configurée (perte de visibilité Google)',
        '❌ Version EN du site non optimisée (98.6% du trafic sur /en mais aucun mot-clé international)',
        '❌ Absence totale sur "romantic dinner paris" (8 900 recherches/mois)',
        '❌ Absence sur "rooftop restaurant paris eiffel tower" (5 800 recherches/mois)',
        '❌ Absence sur "fine dining near eiffel tower" (12 000 recherches/mois)',
        '❌ Pas de contenu en ES/PT alors que Paris = 15M touristes/an',
        '❌ 0 backlink depuis des médias internationaux (Condé Nast, Travel+Leisure...)'
      ]
    },
    opportunities: {
      potential: 'Potentiel de +300% de trafic international en 6 mois',
      estimatedImpact: '180-250 réservations supplémentaires/mois (estimé 18k€-25k€ CA/mois)',
      timeline: '3-6 mois pour voir les premiers résultats significatifs'
    },
    recommendations: {
      quick_wins: [
        'Implémenter hreflang FR/EN/ES/PT immédiatement',
        'Créer 12 landing pages locales (Eiffel Tower, Champs-Elysées, Rooftop)',
        'Optimiser /en avec contenus "rooftop dining", "romantic dinner", "special occasions"',
        'Mettre en avant l\'atout "rooftop vue Tour Eiffel" (7300 recherches/mois)',
        'Lancer campagne Google Business Profile en anglais'
      ],
      strategic_actions: [
        'Créer cluster de contenu "Fine Dining Paris" (15 articles)',
        'Développer partenariats avec concierges hôtels 5*',
        'Obtenir features dans Michelin Guide International, Forbes Travel',
        'Programme de fidélité touristes (retour clients internationaux)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) minimum pour couvrir 3 langues'
    },
    kpis: [
      { name: 'Trafic international', current: '7%', target: '45%', gap: '+38 points' },
      { name: 'Mots-clés EN/ES/PT', current: '2', target: '150+', gap: '+148 mots-clés' },
      { name: 'Réservations touristes', current: '~20/mois', target: '200+/mois', gap: 'x10' },
      { name: 'Backlinks internationaux', current: '0', target: '25', gap: '+25 liens' }
    ]
  },
  {
    id: 'local_seo_gmb',
    name: 'Optimisation Google My Business & Visibilité Locale',
    objective: 'Maximiser la visibilité dans le Local Pack et capter les recherches "restaurant près de moi"',
    currentState: {
      score: 45,
      status: 'warning',
      analysis: '⚠️ FICHE EXISTANTE MAIS SOUS-EXPLOITÉE : GMB actif (4.5★, 818 avis) MAIS seulement 75% de complétude. Vous perdez 40-60% de visibilité potentielle. Posts irréguliers (4/12 ce mois), seulement 45 photos (concurrents : 150+), taux de réponse 75% (doit être 100%). Opportunité : passer de 45/100 à 90/100 = +150% de visibilité locale.',
      findings: [
        '⚠️ Fiche GMB incomplète (75%) - perte de visibilité algorithmique',
        '❌ Activité posts faible : 4/12 publiés ce mois (Google pénalise l\'inactivité)',
        '❌ Seulement 45 photos (minimum 100+ pour restaurant premium)',
        '⚠️ Taux réponse avis 75% (doit être 100% en -24h)',
        '❌ Pas de posts en anglais/espagnol (clientèle internationale)',
        '❌ Section Q&A non renseignée (opportunité Featured Snippet)',
        '❌ Menu non uploadé dans GMB (recherche "menu le 39v")',
        '❌ Attributs manquants (terrasse, parking, wifi, dress code)',
        '❌ Pas d\'optimisation recherche vocale',
        '❌ Horaires spéciaux (événements) non mis à jour'
      ]
    },
    opportunities: {
      potential: 'Apparaître dans le Top 3 Local Pack peut générer +400% de visibilité',
      estimatedImpact: '150-250 réservations/mois supplémentaires via recherche locale (15k€-25k€ CA/mois)',
      timeline: '1-2 mois pour setup + 3 mois pour optimisation complète'
    },
    recommendations: {
      quick_wins: [
        'Compléter les 25% manquants de la fiche GMB (attributs, horaires, services)',
        'Importer 60+ photos supplémentaires (objectif 100+ total)',
        'Répondre à 100% des avis en -24h (actuellement 75%)',
        'Passer à 12 posts/mois réguliers (actuellement 4/12)',
        'Ajouter section Q&A avec 20+ questions/réponses',
        'Uploader menu complet dans GMB',
        'Posts multilingues EN/ES pour touristes'
      ],
      strategic_actions: [
        'Programme systématique de récolte d\'avis post-service',
        'Création de 15 landing pages géolocalisées (quartiers Paris)',
        'Intégration Widget réservation directement dans GBP',
        'Partenariat avec plateformes avis (TripAdvisor, TheFork, Yelp)',
        'Optimisation pour assistants vocaux (Google Assistant, Siri, Alexa)'
      ],
      investment_needed: 'Pack Essentiel (1000€/mois) + formation équipe'
    },
    kpis: [
      { name: 'Complétude GBP', current: '75%', target: '100%', gap: '+25%' },
      { name: 'Avis Google', current: '818', target: '1000+', gap: '+182 avis' },
      { name: 'Note moyenne', current: '4.5★', target: '4.7★', gap: '+0.2★' },
      { name: 'Taux de réponse', current: '75%', target: '100%', gap: '+25%' },
      { name: 'Posts/mois', current: '4', target: '12', gap: '+8 posts' },
      { name: 'Photos GMB', current: '45', target: '100+', gap: '+55 photos' }
    ]
  },
  {
    id: 'privatization_groups',
    name: 'Développement Privatisations & Groupes',
    objective: 'Positionner Le 39V comme LA destination pour événements corporate et privatisations haut de gamme',
    currentState: {
      score: 20,
      status: 'critical',
      analysis: '⚠️ POTENTIEL INEXPLOITÉ : Page /privatisations existe mais 0 visibilité SEO. Vous ratez le marché corporate (50k€-200k€ par événement). Aucun positionnement sur "privatisation restaurant paris", "corporate event venue paris", "private dining paris".',
      findings: [
        '❌ Page privatisation non indexée / non optimisée',
        '❌ 0 mot-clé positionné sur la thématique "événementiel"',
        '❌ Pas de landing dédiée en anglais pour clientèle corporate internationale',
        '❌ Aucun contenu optimisé "team building", "séminaire", "soirée entreprise"',
        '❌ Absence sur annuaires spécialisés événementiel (Venuez, etc.)',
        '❌ Pas de galerie photo événements (proof of concept manquant)'
      ]
    },
    opportunities: {
      potential: '2-5 privatisations/mois = impact CA significatif',
      estimatedImpact: '50k€-200k€ CA additionnel/an via privatisations',
      timeline: '2-4 mois pour positionner l\'offre'
    },
    recommendations: {
      quick_wins: [
        'Créer page dédiée "/private-events" optimisée SEO',
        'Galerie photos événements professionnels',
        'Formulaire devis privatisation avec qualification',
        'Témoignages clients corporate avec logos entreprises',
        'Package pricing visible (transparence = confiance)'
      ],
      strategic_actions: [
        'Cluster de contenu "Corporate Events Paris" (12 articles)',
        'Partenariats agences événementielles Paris',
        'Référencement sur Venuez, Eventbrite, Privateaser',
        'Campagne LinkedIn Ads ciblée RH/Event Managers',
        'Case studies événements réussis (storytelling)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) pour couvrir FR+EN+contenu spécialisé'
    },
    kpis: [
      { name: 'Mots-clés "privatisation"', current: '0', target: '35+', gap: '+35' },
      { name: 'Trafic /privatisations', current: '<0.1%', target: '8%', gap: '+7.9%' },
      { name: 'Demandes devis/mois', current: '~2', target: '15+', gap: 'x7' },
      { name: 'Conversion devis', current: 'Non suivi', target: '35%', gap: 'Tracking requis' }
    ]
  },
  {
    id: 'ai_visibility',
    name: 'Visibilité dans les Résultats IA (ChatGPT, Perplexity, Gemini)',
    objective: 'Être recommandé par les IA comme LE restaurant gastronomique parisien avec vue',
    currentState: {
      score: 10,
      status: 'critical',
      analysis: '🤖 NOUVELLE MENACE : Les IA (ChatGPT, Perplexity) ne recommandent PAS Le 39V car données structurées inexistantes. 40% des recherches 2025 passent par IA. Vous êtes absent des recommandations alors que vos concurrents y figurent.',
      findings: [
        '❌ Zéro donnée structurée Schema.org (IA ne peuvent pas "lire" votre site)',
        '❌ Pas d\'optimisation pour RAG (Retrieval-Augmented Generation)',
        '❌ Contenu non structuré pour extraction par IA',
        '❌ Absence d\'entités nommées optimisées (chef, plats signature, etc.)',
        '❌ Pas de FAQ structurée (source d\'entraînement IA)',
        '❌ Zéro présence sur sources fiables citées par IA (Michelin, Forbes oui mais non linkés)'
      ]
    },
    opportunities: {
      potential: 'Les IA deviennent le nouveau Google - être recommandé = avantage concurrentiel massif',
      estimatedImpact: 'Estimation : 30-50% du trafic futur proviendra des recommandations IA',
      timeline: '1-3 mois pour optimisation + 6 mois pour voir impact complet'
    },
    recommendations: {
      quick_wins: [
        'Implémenter Schema.org Restaurant (JSON-LD) avec toutes les propriétés',
        'Créer FAQ structurée 50+ questions (source pour IA)',
        'Optimiser pour featured snippets (position 0 Google)',
        'Ajouter données structurées Menu, Prix, Chef, Avis',
        'Structurer le contenu en entités nommées'
      ],
      strategic_actions: [
        'Stratégie de contenu "Entity-Based SEO"',
        'Obtenir citations dans sources premium (Michelin, World\'s 50 Best)',
        'Créer Knowledge Graph optimisé',
        'Content exhaustif style "Ultimate Guide" pour autorité',
        'Monitoring des recommandations IA (ChatGPT, Perplexity, Gemini)'
      ],
      investment_needed: 'Pack Signature (3500€/mois) pour développement complet Entity SEO'
    },
    kpis: [
      { name: 'Schema.org implémenté', current: 'Non', target: 'Complet', gap: 'URGENT' },
      { name: 'Recommandations IA', current: '0%', target: '80%', gap: '+80%' },
      { name: 'Featured Snippets', current: '0', target: '12+', gap: '+12' },
      { name: 'Knowledge Graph', current: 'Incomplet', target: 'Enrichi', gap: 'À développer' }
    ]
  },
  {
    id: 'review_reputation',
    name: 'Stratégie Avis Clients & E-réputation',
    objective: 'Atteindre 1000+ avis 5 étoiles et passer de 4.5★ à 4.7★+ pour dominer les classements',
    currentState: {
      score: 55,
      status: 'warning',
      analysis: '⭐ BIEN MAIS PEUT MIEUX FAIRE : 818 avis Google (4.5★) = bonne base MAIS croissance trop lente (besoin 15-20 nouveaux avis/mois), taux de réponse 75% insuffisant (concurrents : 95%+), engagement social faible (19 partages Facebook). Opportunité : atteindre 1000 avis = boost algorithmique Google.',
      findings: [
        '✅ Base solide : 818 avis Google (4.5★)',
        '⚠️ Croissance avis trop lente (~10/mois vs besoin 20/mois)',
        '❌ Taux de réponse 75% (doit être 100% en -24h)',
        '❌ Engagement social très faible (19 partages, 14 likes Facebook)',
        '❌ Pas de système de récolte d\'avis automatisé',
        '⚠️ TripAdvisor : présent mais sous-optimisé (20 backlinks seulement)',
        '❌ Pas de partenariat TheFork (0 avis vérifiés)',
        '❌ Aucune stratégie UGC (User Generated Content)',
        '❌ Pas de monitoring e-réputation 24/7'
      ]
    },
    opportunities: {
      potential: 'Un restaurant avec 100+ avis 4.5★ convertit 3x mieux qu\'un restaurant sans avis',
      estimatedImpact: '+200% de taux de conversion sur les réservations en ligne',
      timeline: '6 mois pour atteindre masse critique de 100 avis'
    },
    recommendations: {
      quick_wins: [
        'Automatiser email post-visite avec lien avis Google (J+1)',
        'QR Code sur table pour avis (incentive : café/digestif offert)',
        'Atteindre taux de réponse 100% en -24h (actuellement 75%)',
        'Optimiser profil TripAdvisor (déjà 20 backlinks)',
        'Widget avis en homepage (social proof)',
        'Campagne SMS post-réservation pour collecte avis'
      ],
      strategic_actions: [
        'Programme VIP pour clients réguliers (ambassadeurs)',
        'Partenariat influenceurs food (Instagram, TikTok)',
        'Monitoring reputation 24/7 avec alertes',
        'Stratégie de réponse aux avis (positifs ET négatifs)',
        'User-Generated Content (photos clients = authenticité)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + outils monitoring avis (~200€/mois)'
    },
    kpis: [
      { name: 'Avis Google', current: '818', target: '1200+', gap: '+382 avis' },
      { name: 'Note moyenne', current: '4.5★', target: '4.7★', gap: '+0.2★' },
      { name: 'Nouveaux avis/mois', current: '~10', target: '20+', gap: 'x2' },
      { name: 'Taux de réponse', current: '75%', target: '100%', gap: '+25%' },
      { name: 'Social engagement', current: '43 total', target: '500+/mois', gap: 'x12' }
    ]
  },
  {
    id: 'technical_seo',
    name: 'Fondations Techniques SEO',
    objective: 'Corriger les erreurs techniques bloquantes qui pénalisent le référencement',
    currentState: {
      score: 35,
      status: 'critical',
      analysis: '🔧 FONDATIONS DÉFAILLANTES : Score SEO 52/100 = pénalités Google actives. Site techniquement en retard de 5 ans. Performance catastrophique (26 assets non cachés), structure HTML manquante (0 headings), optimisation mobile déficiente.',
      findings: [
        '❌ AUCUN heading HTML (H1-H6) = Google ne comprend pas la structure',
        '❌ Meta description 1427 caractères (au lieu de 160) = tronquée partout',
        '❌ 4 images sur 7 sans ALT = accessibilité et SEO dégradés',
        '❌ Aucun sitemap XML = indexation aléatoire par Google',
        '❌ 26 fichiers non cachés = temps de chargement x3 plus lent',
        '❌ Images non optimisées (pas de WebP, pas de lazy loading)',
        '❌ Contraste couleurs insuffisant = pénalité accessibilité',
        '❌ Tap targets trop petits sur mobile = UX dégradée'
      ]
    },
    opportunities: {
      potential: 'Corriger ces erreurs = gain immédiat de 15-25 positions dans Google',
      estimatedImpact: '+150% de trafic organique en 2-3 mois post-correctifs',
      timeline: '1 mois pour corrections critiques'
    },
    recommendations: {
      quick_wins: [
        'Ajouter structure H1-H6 sur toutes les pages',
        'Réduire meta description à 155 caractères',
        'Attributs ALT sur toutes les images',
        'Créer et soumettre sitemap XML',
        'Activer cache navigateur (1 ligne .htaccess)',
        'Convertir images en WebP',
        'Fix contraste footer (4.5:1 minimum)'
      ],
      strategic_actions: [
        'Audit Core Web Vitals complet',
        'Migration vers CDN (Cloudflare)',
        'Lazy loading images + scripts',
        'Optimisation CSS/JS (code splitting)',
        'Implémentation PWA (offline mode)',
        'Monitoring performance continue (Lighthouse CI)'
      ],
      investment_needed: 'Dev technique : 5-8k€ one-time + Pack Premium pour suivi'
    },
    kpis: [
      { name: 'Score SEO', current: '52/100', target: '85+/100', gap: '+33 points' },
      { name: 'Core Web Vitals', current: 'Échec', target: 'Bon', gap: 'Critique' },
      { name: 'Temps de chargement', current: '~4s', target: '<1.5s', gap: '-2.5s' },
      { name: 'Erreurs SEO', current: '18', target: '0', gap: '-18 erreurs' }
    ]
  },
  {
    id: 'content_strategy',
    name: 'Stratégie de Contenu & Autorité',
    objective: 'Devenir LA référence contenu pour la gastronomie parisienne haut de gamme',
    currentState: {
      score: 25,
      status: 'warning',
      analysis: '📝 OPPORTUNITÉ MANQUÉE : Site vitrine statique sans blog ni contenu. 0 article, 0 guide, 0 storytelling. Impossible de ranker sur requêtes informationnelles à fort volume. Vos concurrents publient 2-4 articles/mois. Vous : 0.',
      findings: [
        '❌ Aucun blog / section actualités',
        '❌ 0 article optimisé SEO',
        '❌ Pas de contenu "guide" (ex: "Guide to Fine Dining in Paris")',
        '❌ Menu non détaillé sur le site (opportunité mots-clés plats)',
        '❌ Pas de storytelling chef / équipe',
        '❌ Section "expériences" sous-exploitée'
      ]
    },
    opportunities: {
      potential: 'Content marketing = x5 trafic organique + positionnement expert',
      estimatedImpact: 'Blog optimisé = 10k-20k visites/mois supplémentaires en 12 mois',
      timeline: '6-12 mois pour voir impact significatif'
    },
    recommendations: {
      quick_wins: [
        'Créer section blog avec 3 articles piliers',
        'Publier "Ultimate Guide to Romantic Dining in Paris"',
        'Détailler menu en ligne avec photos + descriptions SEO',
        'Page chef avec bio + interview',
        'Calendrier événements (dégustations, menu saisonnier)'
      ],
      strategic_actions: [
        'Plan éditorial 12 mois (2 articles/mois minimum)',
        'Clusters thématiques (Fine Dining, Wine Pairing, Paris Views)',
        'Guest posting sur blogs food internationaux',
        'Vidéos YouTube (cuisine, coulisses, interviews)',
        'Newsletter VIP avec contenu exclusif'
      ],
      investment_needed: 'Pack Premium (2000€/mois) incluant production contenu'
    },
    kpis: [
      { name: 'Articles publiés', current: '0', target: '24/an', gap: '+24' },
      { name: 'Trafic blog', current: '0', target: '15k/mois', gap: '+15k' },
      { name: 'Temps sur site', current: '~2min', target: '5min+', gap: '+3min' },
      { name: 'Pages/session', current: '1.8', target: '4.5+', gap: '+2.7' }
    ]
  },
  {
    id: 'multi_channel',
    name: 'Diversification Canaux de Réservation',
    objective: 'Réduire la dépendance à un seul canal et maximiser la visibilité multi-plateformes',
    currentState: {
      score: 30,
      status: 'warning',
      analysis: '📱 MONO-CANAL = RISQUE : Dépendance excessive au site web. Absence sur plateformes clés (TheFork, OpenTable, Resy). Les clients réservent là où ils cherchent. Si vous n\'y êtes pas = réservation perdue.',
      findings: [
        '❌ Pas de présence TheFork (leader EU)',
        '❌ Absent d\'OpenTable (clientèle US)',
        '❌ Non référencé Resy (millennials aisés)',
        '❌ Pas d\'intégration Google Reserve',
        '❌ Réservation widget peu visible site web',
        '❌ Pas de click-to-call optimisé mobile'
      ]
    },
    opportunities: {
      potential: 'Multi-canal = +60% de points de contact = +40% de réservations',
      estimatedImpact: '80-120 couverts supplémentaires/mois (8k€-12k€ CA/mois)',
      timeline: '1-2 mois pour déploiement multi-plateformes'
    },
    recommendations: {
      quick_wins: [
        'Inscription immédiate TheFork + optimisation profil',
        'Setup OpenTable pour clientèle internationale',
        'Intégration Google Reserve dans GBP',
        'Widget réservation visible homepage (above the fold)',
        'Click-to-call optimisé mobile avec tracking'
      ],
      strategic_actions: [
        'Dashboard centralisé réservations (pas de double booking)',
        'Programme de fidélité cross-plateformes',
        'Analytics attribution par canal',
        'A/B testing formulaires réservation',
        'Remarketing sur abandons de réservation'
      ],
      investment_needed: 'Pack Essentiel (1000€/mois) + intégrations techniques (~2k€)'
    },
    kpis: [
      { name: 'Plateformes actives', current: '1', target: '5+', gap: '+4' },
      { name: 'Réservations online', current: '~40/mois', target: '150+/mois', gap: 'x4' },
      { name: 'Taux conversion', current: '~2%', target: '8%+', gap: '+6%' },
      { name: 'Coût par réservation', current: 'Non suivi', target: '<15€', gap: 'Tracking requis' }
    ]
  },
  {
    id: 'chatbot_ai',
    name: 'Service Client IA 24/7 Multilingue',
    objective: 'Capturer les réservations hors horaires et répondre instantanément aux questions en 4 langues',
    currentState: {
      score: 0,
      status: 'critical',
      analysis: '🤖 OPPORTUNITÉ NON EXPLOITÉE : Aucun système de réponse automatique. Vous perdez des demandes hors horaires (23h, dimanche, jours fériés). Les clients vont sur le site, posent une question, pas de réponse immédiate = risque qu\'ils réservent chez le concurrent.',
      findings: [
        '❌ Aucun chatbot ou réponse automatique',
        '❌ Visiteurs hors horaires (60%) = perdus',
        '❌ Barrière langue pour touristes (questions en EN/ES/PT sans réponse)',
        '❌ Temps de réponse email : 4-24h (trop lent, client déjà réservé ailleurs)',
        '❌ Questions privatisation/groupe sans réponse immédiate = leads perdus',
        '❌ Pas de qualification automatique des demandes'
      ]
    },
    opportunities: {
      potential: 'Chatbot IA = +15-25% de taux de conversion sur le site web',
      estimatedImpact: '30-50 réservations supplémentaires/mois (3k€-5k€ CA/mois)',
      timeline: '7-10 jours pour mise en service du chatbot'
    },
    recommendations: {
      quick_wins: [
        'Déployer chatbot IA multilingue (FR/EN/ES/PT)',
        'Nourrir IA avec FAQ, menu, horaires, infos privatisation',
        'Intégrer lien réservation directe dans conversations',
        'Configurer collecte automatique emails/téléphones',
        'Analytics conversations pour optimiser réponses'
      ],
      strategic_actions: [
        'Intégration chatbot avec système de réservation (temps réel)',
        'Personnalisation avancée selon profil visiteur',
        'A/B testing des messages et tonalité',
        'Formation équipe à l\'utilisation du dashboard chatbot',
        'Optimisation continue basée sur analytics'
      ],
      investment_needed: 'Pack Premium (2000€/mois) minimum - Chatbot inclus'
    },
    kpis: [
      { name: 'Disponibilité', current: '0%', target: '24/7', gap: 'Chatbot requis' },
      { name: 'Temps de réponse', current: '4-24h', target: '<30s', gap: '-99%' },
      { name: 'Conversions site', current: '~2%', target: '4-5%', gap: '+2-3%' },
      { name: 'Réservations 24/7', current: '0', target: '30-50/mois', gap: 'Chatbot requis' },
      { name: 'Langues supportées', current: '0', target: '4 (FR/EN/ES/PT)', gap: '+4 langues' }
    ]
  }
];

// Projets stratégiques Café Max (Données réelles SEMrush + Woorank)
export const strategicProjectsCafeMax: StrategicProject[] = [
  {
    id: 'seo_visibility_crisis',
    name: 'CRISE DE VISIBILITÉ : Position 48.59 + Trafic 15/mois',
    objective: 'Sortir de l\'invisibilité Google et atteindre page 1 sur requêtes cibles',
    currentState: {
      score: 15,
      status: 'critical',
      analysis: '🚨 SITUATION CRITIQUE : Position moyenne 48.59 = page 5 de Google. Trafic catastrophique de 15 visiteurs/mois. Part de voix 2.18% = quasi-invisibilité. 33% des mots-clés hors Top 100. Concurrent La Fontaine de Mars reçoit 800 visiteurs/mois (53x plus). Perte estimée de -45 000€ CA/an.',
      findings: [
        '❌ Position moyenne 48.59 (page 5) - INVISIBLE',
        '❌ Trafic 14.76 visiteurs/mois vs 800 pour concurrent (-98%)',
        '❌ Seulement 28% des mots-clés en page 1',
        '❌ 33% des mots-clés hors Top 100 (invisibles)',
        '❌ Part de voix 2.18% = inexistence digitale',
        '❌ NON POSITIONNÉ sur "bistrot invalides" (1200 recherches/mois)',
        '❌ NON POSITIONNÉ sur "restaurant 7ème" (3200 recherches/mois)',
        '❌ Version EN position 39 (mal optimisée)'
      ]
    },
    opportunities: {
      potential: 'Potentiel de +1 767% de trafic en 3 mois',
      estimatedImpact: '280 visiteurs/mois = +8 réservations = +6 240€ CA/an',
      timeline: '3 mois pour atteindre page 1 sur mots-clés principaux'
    },
    recommendations: {
      quick_wins: [
        'Corriger meta description (1081 caractères → 160)',
        'Optimiser title tags avec mots-clés stratégiques',
        'Créer robots.txt + sitemap XML',
        'Ajouter attributs ALT sur 20 images',
        'Implémenter Schema.org Restaurant'
      ],
      strategic_actions: [
        'Créer 4 pages piliers (Bistrot Invalides, Histoire, Chef, Terrasse)',
        'Lancer blog avec 2 articles/mois',
        'Campagne netlinking (objectif 52 backlinks en 3 mois)',
        'Optimiser GMB (3 posts/semaine, 50 nouvelles photos)'
      ],
      investment_needed: 'Pack PREMIUM (2800€/mois) x 3 mois = 8 400€'
    },
    kpis: [
      { name: 'Position moyenne', current: '48.59', target: '<15', gap: '-33 positions' },
      { name: 'Trafic organique', current: '15/mois', target: '280/mois', gap: '+1 767%' },
      { name: 'Mots-clés Top 10', current: '22 (28%)', target: '58 (73%)', gap: '+164%' },
      { name: 'Part de voix', current: '2.18%', target: '8-10%', gap: '+6 points' }
    ]
  },
  {
    id: 'technical_seo_emergency',
    name: 'URGENCE TECHNIQUE : Score Woorank 45/100',
    objective: 'Corriger les 16 erreurs SEO critiques bloquantes',
    currentState: {
      score: 20,
      status: 'critical',
      analysis: '🚨 URGENCE ABSOLUE : Score technique 45/100 = pénalités Google actives. 16 erreurs critiques P0 non corrigées. Aucun robots.txt, aucun sitemap, aucune donnée structurée, 20 images sans ALT. Meta description 7x trop longue. Backlinks Score "BAD" (12 seulement). Site non optimisé pour Google 2025.',
      findings: [
        '❌ Meta description 1081 caractères (vs 160 max) - CRITIQUE',
        '❌ Robots.txt ABSENT - Google crawle n\'importe quoi',
        '❌ Sitemap XML ABSENT - Google ne connaît pas le site',
        '❌ Schema.org ABSENT - Pas de rich snippets',
        '❌ Balises canoniques ABSENTES - Risque duplicate content',
        '❌ 20 images/22 sans ALT - Invisibles Google Images',
        '❌ Hreflang ABSENT - Version EN/FR confondues',
        '❌ Open Graph ABSENT - Partages sociaux sans image',
        '❌ HSTS non configuré - Vulnérabilité sécurité',
        '❌ Backlinks : 12 seulement (vs 450 pour concurrent)'
      ]
    },
    opportunities: {
      potential: 'Score Woorank 45 → 78 en 1 mois',
      estimatedImpact: 'Déblocage crawl Google + +35% CTR rich snippets',
      timeline: '2-4 semaines pour corrections techniques'
    },
    recommendations: {
      quick_wins: [
        'JOUR 1 : Réécrire meta description en 160 caractères',
        'JOUR 1 : Créer robots.txt et sitemap.xml',
        'JOUR 2 : Implémenter Schema Restaurant complet',
        'JOUR 3 : Ajouter ALT sur toutes les images',
        'JOUR 5 : Activer compression + cache',
        'JOUR 7 : Implémenter hreflang FR/EN'
      ],
      strategic_actions: [
        'Campagne backlinks : 40 liens en 6 mois',
        'Partenariats hôtels (10 backlinks)',
        'Relations Presse (ParisbyMouth, Le Fooding)',
        'Influenceurs food Paris (8 backlinks)'
      ],
      investment_needed: 'Pack ESSENTIEL (1500€/mois) suffisant pour technique'
    },
    kpis: [
      { name: 'Score Woorank', current: '45/100', target: '78/100', gap: '+73%' },
      { name: 'Erreurs critiques', current: '16', target: '0', gap: '-16' },
      { name: 'Backlinks', current: '12', target: '52', gap: '+333%' },
      { name: 'Autorité domaine', current: '18', target: '35', gap: '+94%' }
    ]
  },
  {
    id: 'local_gmb_optimization',
    name: 'LOCAL SEO : 18% de présence Local Pack seulement',
    objective: 'Dominer les recherches locales Invalides / 7ème arrondissement',
    currentState: {
      score: 35,
      status: 'warning',
      analysis: '⚠️ SOUS-PERFORMANCE LOCALE : Fiche GMB à 62% de complétude. Présence dans seulement 14/79 Local Packs (18%). 82% d\'opportunités manquées. 45 photos seulement (vs 100 recommandées). Posts irréguliers (8 publiés). Section Q&A non renseignée. Taux réponse avis 68% (vs 90% requis).',
      findings: [
        '⚠️ Présence Local Pack : 14/79 (18%) - 82% opportunités manquées',
        '❌ Complétude GMB 62% - Pénalité algorithme Google',
        '❌ 45 photos seulement (minimum 100+)',
        '❌ Posts GMB : 8 publiés vs 3/semaine requis',
        '❌ Section Q&A vide - Opportunité FAQ',
        '⚠️ Taux réponse avis 68% (objectif 90%+)',
        '❌ Attributs manquants (WiFi, terrasse, PMR)',
        '❌ Citations : 45 vs 100 recommandées'
      ]
    },
    opportunities: {
      potential: 'Local Pack 18% → 53% = +200% de visibilité locale',
      estimatedImpact: '+40 découvertes spontanées/jour = +24 réservations/mois',
      timeline: '1-2 mois pour amélioration visible'
    },
    recommendations: {
      quick_wins: [
        'Upload 50 photos professionnelles (shoot photo)',
        'Compléter tous les attributs GMB',
        'Renseigner 20 Q&A proactives',
        'Répondre à 100% des avis sous 24h',
        'Planifier 3 posts GMB/semaine pendant 3 mois'
      ],
      strategic_actions: [
        'Campagne avis : +100 avis Google en 3 mois',
        'Ajouter 55 citations (Pages Jaunes, Yelp, etc.)',
        'Optimiser pour recherche vocale',
        'Posts GMB multilingues (FR + EN)'
      ],
      investment_needed: 'Pack PREMIUM inclut gestion GMB complète'
    },
    kpis: [
      { name: 'Présence Local Pack', current: '14 (18%)', target: '42 (53%)', gap: '+200%' },
      { name: 'Complétude GMB', current: '62%', target: '95%', gap: '+33 points' },
      { name: 'Photos GMB', current: '45', target: '95', gap: '+111%' },
      { name: 'Avis Google', current: '500', target: '600', gap: '+20%' }
    ]
  },
  {
    id: 'hotel_partnerships',
    name: 'PARTENARIATS HÔTELIERS : 15 hôtels 4* dans un rayon de 2km',
    objective: 'Capter la clientèle hôtelière via recommandations conciergeries',
    currentState: {
      score: 5,
      status: 'critical',
      analysis: '🚨 OPPORTUNITÉ MAJEURE NON EXPLOITÉE : 15 hôtels 4* identifiés dans 2km (565 chambres total). Taux occupation moyen 76% = 12 750 clients/mois potentiels. AUCUN partenariat formalisé actuellement (sauf mention informelle Latour-Maubourg). Concurrents La Fontaine de Mars ont 8+ partenariats actifs. Perte estimée : 152 réservations/mois = 9 880€/mois = 118 560€/an.',
      findings: [
        '❌ 0 partenariat hôtelier formalisé (vs 8+ pour concurrent)',
        '🎯 15 hôtels 4* identifiés à moins de 2km',
        '🏨 565 chambres total × 76% occupation = 12 750 guests/mois',
        '💰 Potentiel : 152 réservations/mois × 65€ = 9 880€/mois',
        '📱 15 numéros WhatsApp collectés pour outreach',
        '✉️ Templates de communication prêts (WhatsApp/Email/Phone)',
        '🤝 Le Walt (même rue) = partenariat PRIORITAIRE',
        '❌ Pas de ligne WhatsApp Business dédiée conciergeries',
        '❌ Pas de menu/supports en format hôtel (multi-langues)',
        '❌ Aucune commission tracking system'
      ]
    },
    opportunities: {
      potential: 'Partenariat avec 10 hôtels = +152 réservations/mois',
      estimatedImpact: '152 réservations × 65€ panier moyen = 9 880€/mois = 118 560€/an',
      timeline: '1-2 mois pour signer 5 premiers partenariats'
    },
    recommendations: {
      quick_wins: [
        'SEMAINE 1 : Contact WhatsApp 5 hôtels prioritaires (Le Walt, Latour-Maubourg, Muguet, Le Tourville, Duquesne)',
        'SEMAINE 1 : Créer supports hôtel (cartes de visite, flyers multilingues)',
        'SEMAINE 2 : Déjeuner découverte offert à 3 concierges',
        'SEMAINE 2 : Activer ligne WhatsApp Business dédiée',
        'SEMAINE 3 : Signature 2 premiers partenariats',
        'SEMAINE 4 : Roll-out 5 hôtels suivants'
      ],
      strategic_actions: [
        'Workflow automatisé : WhatsApp J1 → Email J3 → Phone J5 → Visite J7',
        'Programme commission : 10€/réservation pour concierges',
        'Reporting mensuel : dashboard réservations par hôtel',
        'Événement trimestriel : soirée dégustation pour concierges partenaires',
        'Menu spécial "Hôtel" : 38€ (vs 45€ normal) avec apéritif offert',
        'Création kit partenaire : room directory insert + QR code réservation'
      ],
      investment_needed: 'Budget : 2 500€ one-shot (supports + 5 déjeuners découverte) + 600€/mois (commissions estimées)'
    },
    kpis: [
      { name: 'Hôtels partenaires', current: '0', target: '10/15', gap: '+10 signés' },
      { name: 'Réservations hôtel', current: '0/mois', target: '152/mois', gap: '+152' },
      { name: 'CA additionnel hôtel', current: '0€/an', target: '118 560€/an', gap: '+118k€' },
      { name: 'Taux conversion contact', current: '0%', target: '67%', gap: '10/15 signés' }
    ]
  }
];

// Projets stratégiques La Fontaine Gaillon (basé sur rapport d'audit)
export const strategicProjectsFontaineGaillon: StrategicProject[] = [
  {
    id: 'domain_architecture',
    name: 'Migration vers Domaine Dédié & Clarification Autorité',
    objective: 'Acquérir lafontainegaillon.com et dédier 100% des signaux SEO à l\'univers sémantique du restaurant',
    currentState: {
      score: 35,
      status: 'warning',
      analysis: '⚠️ DILUTION D\'AUTORITÉ : Site hébergé en sous-répertoire fitz-group.fr/fontaine-gaillon-paris. Le domaine racine doit porter l\'autorité pour plusieurs concepts très différents (Gaillon, Savoy, Fitzgerald, Abstinence...). Pour Google, le thème global devient flou. Cette architecture crée une cannibalisation sémantique qui limite le potentiel SEO.',
      findings: [
        '❌ Site en sous-domaine fitz-group.fr - autorité thématique diluée',
        '⚠️ Navigation inter-sites (FITZGERALD, ABSTINENCE...) crée confusion pour robots',
        '❌ Footers et headers partagés avec coordonnées multiples établissements',
        '⚠️ Topic Authority fragmentée entre 5+ concepts différents',
        '❌ lafontainegaillon.com disponible mais non activé'
      ]
    },
    opportunities: {
      potential: 'Domaine dédié = +30-50% autorité SEO + clarté pour algorithmes',
      estimatedImpact: '+40 positions moyennes sur requêtes génériques + meilleure indexation',
      timeline: '2-3 mois pour migration et consolidation autorité'
    },
    recommendations: {
      quick_wins: [
        'Acquérir lafontainegaillon.com immédiatement',
        'Configurer redirection 301 depuis ancien vers nouveau domaine',
        'Mettre à jour Google Search Console',
        'Supprimer navigation inter-sites du header/footer',
        'Nettoyer signaux mixtes (adresses, téléphones multiples)'
      ],
      strategic_actions: [
        'Créer architecture Topic Cluster dédiée 100% Gaillon',
        'Développer contenu spécifique (terrasse, salons, patrimoine)',
        'Stratégie de backlinks vers nouveau domaine',
        'Refonte complète identité visuelle digitale autonome'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + Migration technique (5k€ one-time)'
    },
    kpis: [
      { name: 'Domain Authority propre', current: '35 (partagé)', target: '45+', gap: '+29%' },
      { name: 'Topic Clarity Score', current: '35%', target: '90%+', gap: '+55 points' },
      { name: 'Organic Traffic', current: '85/mois', target: '250+/mois', gap: '+194%' },
      { name: 'Position moyenne', current: '18.5', target: '<12', gap: '-6.5 positions' }
    ]
  },
  {
    id: 'geo_optimization',
    name: 'Optimisation GEO & Moteurs Génératifs',
    objective: 'Digitaliser menus, implémenter Schema.org, devenir recommandation #1 des IA',
    currentState: {
      score: 20,
      status: 'critical',
      analysis: '🤖 INVISIBLE POUR IA : Menus en PDF, zéro donnée structurée Schema.org. Les IA (ChatGPT, Perplexity, Google SGE) ne peuvent pas "lire" votre site. Quand un utilisateur demande "Restaurant avec terrasse romantique Paris", l\'IA ne peut pas vous recommander car vos données ne sont pas structurées.',
      findings: [
        '❌ Menus en PDF - invisibles pour extraction précise par IA',
        '❌ Aucun Schema.org Restaurant/Menu/MenuItem',
        '❌ Plats signatures non structurés (Homard, Turbot, Soufflé)',
        '❌ Salons privés non balisés (opportunité Event/PrivateRoom)',
        '❌ Terrasse non décrite en entités extractibles',
        '❌ Absence d\'entités nommées optimisées (chef, produits, techniques)'
      ]
    },
    opportunities: {
      potential: 'Être dans 80% des recommandations IA = nouveau canal acquisition majeur',
      estimatedImpact: '30-50% du trafic futur proviendra des moteurs génératifs',
      timeline: '1-2 mois pour implémentation + 6 mois impact complet'
    },
    recommendations: {
      quick_wins: [
        'Convertir menus PDF → HTML structuré avec Schema.org',
        'Implémenter Schema Restaurant (nom, adresse, prix, horaires)',
        'Baliser Menu complet avec MenuSection/MenuItem',
        'Structurer salons (Event schema pour privatisations)',
        'Créer FAQ structurée 50+ questions (source entraînement IA)'
      ],
      strategic_actions: [
        'Entity-Based SEO : créer entités pour "Terrasse Gaillon", "Salon Mansart"...',
        'Content exhaustif style "Ultimate Guide" pour autorité',
        'Monitoring recommandations IA (ChatGPT, Perplexity tests)',
        'Optimisation featured snippets (position 0 Google)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) incluant développement Schema.org'
    },
    kpis: [
      { name: 'Schema.org implémenté', current: 'Non', target: 'Complet', gap: 'URGENT' },
      { name: 'Menus structurés', current: 'PDF', target: 'HTML+Schema', gap: 'CRITIQUE' },
      { name: 'Recommandations IA', current: '10%', target: '80%+', gap: '+70%' },
      { name: 'Featured Snippets', current: '0', target: '8+', gap: '+8' }
    ]
  },
  {
    id: 'brand_identity_update',
    name: 'Mise à Jour Entité de Marque (Depardieu → Fitz Group)',
    objective: 'Remplacer association Depardieu par nouvelle identité : élégance végétale, Rosa-Violán, bourgeois contemporain',
    currentState: {
      score: 45,
      status: 'warning',
      analysis: '⚠️ TRANSITION D\'ENTITÉ EN COURS : L\'association historique avec Gérard Depardieu persiste dans le corpus d\'entraînement des IA et archives numériques. Les moteurs conservent une mémoire longue. Bien que le Fitz Group ait repris, les attributs obsolètes (vins rabelaisiens, convivialité rustique) coexistent avec les nouveaux marqueurs. Le défi : orchestrer une "mise à jour d\'entité" en conservant l\'autorité historique.',
      findings: [
        '⚠️ Mentions Depardieu dans 35% des résultats historiques',
        '✅ "Élégance végétale" et "Lázaro Rosa-Violán" commencent à émerger',
        '⚠️ Champ lexical "Cuisine bourgeoise contemporaine" faible',
        '✅ Profondeur historique 1802 bien présente',
        '❌ Pas de contenu structuré sur nouvelle identité Fitz Group'
      ]
    },
    opportunities: {
      potential: 'Transition d\'entité réussie = nouvelle clientèle sans perdre ancienne',
      estimatedImpact: 'Rajeunissement image marque + ouverture segment végétal/wellness',
      timeline: '6-12 mois pour consolidation nouvelle identité'
    },
    recommendations: {
      quick_wins: [
        'Créer page "Notre Histoire" : 1802 → Depardieu (héritage) → Fitz Group (renouveau)',
        'Content marketing focus "Élégance végétale", "Design Rosa-Violán"',
        'Mettre en avant cuisine bourgeoise contemporaine dans tous les contenus',
        'Relations presse ciblées design/architecture (mentions Rosa-Violán)',
        'Shooting photo professionnel nouvelle identité'
      ],
      strategic_actions: [
        'Stratégie de contenu long terme : 2 articles/mois pendant 12 mois',
        'Partenariats influenceurs design/architecture',
        'Obtenir features dans médias lifestyle (AD, Elle Décoration)',
        'Campagne sociale mettant en avant nouveau décor',
        'Programme ambassadeurs pour clients réguliers (transition douce)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + Relations presse (1500€/mois)'
    },
    kpis: [
      { name: 'Mentions "Rosa-Violán"', current: '15%', target: '60%+', gap: '+45%' },
      { name: 'Mentions "Depardieu"', current: '35%', target: '<15%', gap: '-20%' },
      { name: 'Brand sentiment "moderne"', current: '40%', target: '75%', gap: '+35%' },
      { name: 'Share of voice "design"', current: '5%', target: '25%', gap: '+20%' }
    ]
  },
  {
    id: 'b2b_privatization',
    name: 'Stratégie B2B Salons Privés & Privatisations',
    objective: 'Positionner les 6 salons historiques comme référence corporate Paris',
    currentState: {
      score: 25,
      status: 'warning',
      analysis: '⚠️ POTENTIEL INEXPLOITÉ : Actif stratégique majeur sous-utilisé. 6 salons privés historiques (Mansart, Krug, Rémy Martin, Gaillon, Louis Le Grand, Emile Zola) avec noms chargés d\'histoire = différenciateur puissant. Mais visibilité SEO B2B quasi-nulle. Aucun positionnement sur "private dining Paris", "corporate event venue".',
      findings: [
        '✅ 6 salons avec noms prestige (asset unique)',
        '❌ Salons non indexables individuellement (pas de pages dédiées)',
        '❌ 0 mot-clé positionné sur thématique "événementiel"',
        '❌ Pas de landing EN pour clientèle corporate internationale',
        '❌ Absence annuaires spécialisés (Venuez, Privateaser)',
        '❌ Pas de galerie photo événements corporate'
      ]
    },
    opportunities: {
      potential: '2-5 privatisations/mois = 50k€-200k€ CA additionnel/an',
      estimatedImpact: 'Ouverture nouveau segment revenus + occupation journée',
      timeline: '2-4 mois pour positionner l\'offre'
    },
    recommendations: {
      quick_wins: [
        'Créer 6 pages dédiées (une par salon) avec photos, capacité, équipements',
        'Page pilier "/privatisations" optimisée FR+EN',
        'Galerie photos événements professionnels + témoignages',
        'Formulaire devis avec qualification automatique',
        'Package pricing transparent (confiance)'
      ],
      strategic_actions: [
        'Référencement Venuez, Privateaser, Eventbrite',
        'Partenariats agences événementielles Paris (10 cibles)',
        'Campagne LinkedIn Ads (RH, Event Managers, EA)',
        'Case studies événements réussis (storytelling)',
        'Programme commission pour apporteurs d\'affaires'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + LinkedIn Ads (1000€/mois)'
    },
    kpis: [
      { name: 'Mots-clés "privatisation"', current: '0', target: '35+', gap: '+35' },
      { name: 'Trafic /privatisations', current: '<0.1%', target: '8%', gap: '+7.9%' },
      { name: 'Demandes devis/mois', current: '~1', target: '15+', gap: 'x15' },
      { name: 'Conversion devis', current: 'Non suivi', target: '35%', gap: 'Setup tracking' }
    ]
  },
  {
    id: 'terrace_seasonal_seo',
    name: 'Exploitation Actif "Terrasse" - SEO Saisonnier',
    objective: 'Dominer requêtes "terrasse paris" en saison haute',
    currentState: {
      score: 60,
      status: 'good',
      analysis: '✅ ACTIF PUISSANT IDENTIFIÉ : La terrasse néoclassique avec fontaine est un différenciateur unique mentionné comme "out of this world", "gorgeous neoclassical fountain". C\'est une entité forte dans le Knowledge Graph. Cependant, exploitation SEO saisonnière non optimisée.',
      findings: [
        '✅ Mentions "terrasse" récurrentes dans avis (signal fort)',
        '✅ Description "îlot de tranquillité" unique à Paris',
        '✅ Facteur décisionnel primaire selon avis clients',
        '⚠️ Pas de content marketing spécifique terrasse',
        '❌ SEO saisonnier non optimisé (pics avril-septembre)',
        '⚠️ Pas de landing dédiée "Terrasse romantique"'
      ]
    },
    opportunities: {
      potential: 'Terrasse = +200 couverts supplémentaires avril-septembre',
      estimatedImpact: '+15k€ CA/mois en saison haute (avril-sept)',
      timeline: '2 mois avant saison (février-mars) pour maximiser impact'
    },
    recommendations: {
      quick_wins: [
        'Créer landing "/terrasse-neoclassique" optimisée (FR+EN)',
        'Content marketing : "10 raisons de déjeuner sur notre terrasse"',
        'Shooting photo professionnel terrasse saisons (printemps/été)',
        'Upload 30+ photos terrasse sur GMB',
        'Posts GMB réguliers mars-septembre focus terrasse'
      ],
      strategic_actions: [
        'Campagne Google Ads saisonnière "terrasse paris" (mars-sept)',
        'Partenariats influenceurs lifestyle printemps',
        'Offres spéciales terrasse (brunch dominical, afterwork...)',
        'Événements terrasse (dégustations vins, soirées jazz...)',
        'Monitoring météo pour activation posts GMB opportunistes'
      ],
      investment_needed: 'Pack Essentiel (1000€/mois) + Google Ads saisonnier (2000€ total)'
    },
    kpis: [
      { name: 'Position "terrasse paris 2"', current: '8', target: '3', gap: '-5 positions' },
      { name: 'Trafic saisonnier avril-sept', current: '+15%', target: '+80%', gap: '+65%' },
      { name: 'Réservations terrasse', current: '60%', target: '85%', gap: '+25%' },
      { name: 'Photos terrasse GMB', current: '~15', target: '50', gap: '+35 photos' }
    ]
  }
];

// Projets stratégiques Hollywood Savoy (basé sur rapport d'audit)
export const strategicProjectsHollywoodSavoy: StrategicProject[] = [
  {
    id: 'reputation_polarization',
    name: 'URGENCE : Résoudre Polarisation E-réputation',
    objective: 'Aligner perception TheFork (6.7 ambiance) avec réalité festive (5/5 Google)',
    currentState: {
      score: 30,
      status: 'critical',
      analysis: '🚨 PARADOXE E-RÉPUTATION : Note ambiance catastrophique 6.7/10 sur TheFork VS évaluations dithyrambiques 5/5 Google ("Superbe soirée", "Ambiance lumineuse"). Ce décalage révèle un "Mismatched Intent" : clients TheFork recherchent promotions/repas calmes, surpris par volume sonore. Clientèle cible (party goers) adore, mais risque que algorithmes pénalisent.',
      findings: [
        '❌ Note ambiance TheFork 6.7/10 (critique)',
        '✅ Note ambiance Google 5/5 (excellent)',
        '⚠️ Avis négatifs "table trop bruyante" récurrents TheFork',
        '✅ Avis positifs "Ambiance lumineuse, festive" Google/Instagram',
        '⚠️ Note globale Google 4.0 (zone risque vs 4.5 seuil excellence)',
        '✅ Service exceptionnel 9.3/10 (compensateur)',
        '❌ Mentions nominatives personnel (Nissrine, Mouloud) = pépites non exploitées'
      ]
    },
    opportunities: {
      potential: 'Remonter note 4.0 → 4.5 = +60% visibilité algorithme Google Maps',
      estimatedImpact: '+40 découvertes/jour = +80 réservations/mois = 6.4k€/mois',
      timeline: '3-6 mois pour stabiliser réputation'
    },
    recommendations: {
      quick_wins: [
        'Programme "Ambassadeurs Festifs" : QR code donné uniquement aux tables appréciant ambiance',
        'Réponse sémantique systématique avis négatifs bruit : "Le Hollywood Savoy est un lieu vivant et festif conçu pour célébrer..."',
        'Mise en avant service exceptionnel (Nissrine, Mouloud) sur GMB et site',
        'Clarifier ambiance sur TheFork : "🎉 Ambiance festive et musicale"',
        'Inciter clients Instagram/TikTok à noter aussi TheFork'
      ],
      strategic_actions: [
        'Segmentation réservations : proposer tables "zone calme" pour repas classiques',
        'Différenciation horaires : Lunch (calme) VS Dinner/Party (festif)',
        'Campagne avis ciblée clientèle festive (influenceurs nightlife)',
        'Monitoring reputation 24/7 avec alertes',
        'Formation équipe : gérer attentes clients (call de confirmation)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + outils monitoring (200€/mois)'
    },
    kpis: [
      { name: 'Note Google', current: '4.0', target: '4.5', gap: '+0.5★' },
      { name: 'Note ambiance TheFork', current: '6.7', target: '8.5', gap: '+27%' },
      { name: 'Nouveaux avis/mois', current: '~15', target: '40+', gap: '+167%' },
      { name: 'Ratio avis positifs', current: '75%', target: '88%', gap: '+13%' }
    ]
  },
  {
    id: 'hybrid_identity_clarification',
    name: 'Clarification Identité Hybride Restaurant/Club',
    objective: 'Aider algorithmes à comprendre la dualité sans confusion de classification',
    currentState: {
      score: 40,
      status: 'warning',
      analysis: '⚠️ DUALITÉ PÉRILLEUSE : Le lieu est à la fois restaurant (Tomahawk, Caviar) et club (DJ sets, Live Show). En SEO local, cette dualité est complexe. Si Google classe le Savoy comme "Night Club", risque de ne pas apparaître pour "Déjeuner d\'affaires". Inversement, classé uniquement "Restaurant Français" = invisibilité pour chercheurs de fête.',
      findings: [
        '⚠️ Classification GMB ambiguë',
        '❌ Pas de séparation claire Restaurant VS Club sur site',
        '✅ Tentative séparation horaires ("Lunch" vs "Party")',
        '❌ "No Photos Please" pour Savoy Club = mystère mais limite UGC',
        '⚠️ Risque cannibalisation mots-clés entre 2 concepts',
        '❌ Pas de stratégie Schema.org distincte (Restaurant + NightClub)'
      ]
    },
    opportunities: {
      potential: 'Clarté identité = +35% de visibilité sur 2 segments (resto + club)',
      estimatedImpact: 'Capter à la fois déjeuners business et soirées festives',
      timeline: '2-3 mois pour restructuration'
    },
    recommendations: {
      quick_wins: [
        'Créer 2 pages distinctes : "/restaurant" et "/savoy-club"',
        'Implémenter double Schema.org : Restaurant + NightClub',
        'Horaires clairs GMB : Déjeuner (11h-15h) / Dîner (19h-23h) / Club (23h-2h)',
        'Sections site séparées avec visuels différents',
        'Landing "/dejeuner-affaires" distincte de "/soiree-festive"'
      ],
      strategic_actions: [
        'Stratégie de contenu bifurquée (blog resto + blog nightlife)',
        'Backlinks segmentés (food blogs + nightlife magazines)',
        'Campagnes publicitaires distinctes par segment',
        'Deux personas marketing clairs (Business Lunch vs Party Goers)',
        'Testing A/B pages selon intention recherche'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + refonte UX/UI (8k€ one-time)'
    },
    kpis: [
      { name: 'Classification GMB', current: 'Mixte', target: 'Double claire', gap: 'Setup' },
      { name: 'Trafic "déjeuner"', current: '25%', target: '40%', gap: '+15%' },
      { name: 'Trafic "soirée"', current: '30%', target: '45%', gap: '+15%' },
      { name: 'Bounce rate distinction', current: 'Non suivi', target: '<35%', gap: 'Setup' }
    ]
  },
  {
    id: 'geo_menu_digitalization',
    name: 'Digitalisation Menu & Plats Signatures pour GEO',
    objective: 'Rendre Tomahawk 1.3kg et Caviar visibles pour requêtes précises IA',
    currentState: {
      score: 15,
      status: 'critical',
      analysis: '🤖 INVISIBLE POUR IA : Menu déjeuner en PDF ("HS - DEJ PM...pdf"). Le Tomahawk 1.3kg est un avantage concurrentiel majeur (plat signature unique) MAIS si cette info est enfermée dans PDF ou image, elle devient invisible pour ChatGPT/Perplexity. Quand utilisateur demande "Quel restaurant propose Tomahawk 2ème arrondissement?", l\'IA ne peut pas répondre.',
      findings: [
        '❌ Menu déjeuner en PDF (non extractible)',
        '✅ Certains éléments en texte ("Oeuf bio mimosa", "Poulet mayonnaise")',
        '❌ Tomahawk 1.3kg 58€ non structuré Schema.org',
        '❌ Caviar non balisé (prix, provenance manquants)',
        '❌ Lobster Roll "street-food haut de gamme" confusion positionnement',
        '❌ Cartes soir/desserts structure complexe ou liens externes'
      ]
    },
    opportunities: {
      potential: 'Menu structuré = recommandation #1 pour requêtes plats spécifiques',
      estimatedImpact: '+25% de trafic via recherches "tomahawk paris", "caviar restaurant"',
      timeline: '3-4 semaines pour digitalisation complète'
    },
    recommendations: {
      quick_wins: [
        'URGENT : Convertir PDF → HTML avec Schema.org Menu',
        'Baliser Tomahawk : Item=Tomahawk | Weight=1.3kg | Price=58€ | Attribute=To Share',
        'Structurer Caviar : Item=Caviar Osciètre | Price=85€ | Origin=France',
        'Pages dédiées plats signatures avec photos professionnelles',
        'FAQ "Qu\'est-ce que le Tomahawk?" pour featured snippet'
      ],
      strategic_actions: [
        'Content marketing plats : "Histoire du Tomahawk", "Comment partager"',
        'Vidéos préparation plats (YouTube, TikTok)',
        'Shooting photo pro menu complet',
        'Optimisation pour recherche vocale ("Ok Google, restaurants tomahawk Paris")',
        'Partenariats influenceurs food (focus plats signatures)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + shooting photo (2k€ one-time)'
    },
    kpis: [
      { name: 'Menu structuré Schema', current: 'Non', target: 'Complet', gap: 'URGENT' },
      { name: 'Position "tomahawk paris"', current: '12', target: '3', gap: '-9 positions' },
      { name: 'Trafic plats signatures', current: '5%', target: '20%', gap: '+15%' },
      { name: 'Conversions requêtes plats', current: 'Non suivi', target: '12%', gap: 'Setup' }
    ]
  },
  {
    id: 'freshness_signal_maximization',
    name: 'Exploitation Signal "Fraîcheur" & Buzz 2024-2025',
    objective: 'Capitaliser sur couverture presse récente massive (Vogue, Timeout)',
    currentState: {
      score: 75,
      status: 'good',
      analysis: '✅ HYPE ACTIF : Couverture presse lifestyle massive 2024-2025 (Vogue, Sortiraparis, Timeout, Cosmopolitan, Paris Secret). Densité articles récents envoie signal "Query Deserves Freshness" aux algorithmes. Pour requêtes liées aux tendances ("Nouveaux restaurants festifs 2025"), le Savoy est prioritaire. Les articles associent concepts visuels forts ("Décor sexy", "Leopard-print carpets") = opportunité recherche multimodale.',
      findings: [
        '✅ Vogue, Timeout, Cosmopolitan = backlinks premium',
        '✅ Articles "Valentine\'s Day 2025", "Best party restaurants"',
        '✅ Inclusion Top Lists = Graal GEO',
        '✅ Signal fraîcheur fort (articles récents)',
        '⚠️ Risque retombée hype dans 6-12 mois',
        '❌ Pas de stratégie pour maintenir momentum'
      ]
    },
    opportunities: {
      potential: 'Convertir hype temporaire en autorité durable',
      estimatedImpact: 'Maintenir trafic élevé post-lancement = stabilité CA',
      timeline: 'Agir maintenant (fenêtre 6 mois pour ancrer position)'
    },
    recommendations: {
      quick_wins: [
        'Créer page "/presse" avec tous les articles',
        'Amplifier articles sur réseaux sociaux',
        'Relations presse continues (1 article majeur/mois)',
        'Partenariats influenceurs lifestyle (maintien visibilité)',
        'Événements mensuels "Savoy Nights" (générateurs de PR)'
      ],
      strategic_actions: [
        'Transformer buzz en contenu durable (guides, histoires)',
        'Programme ambassadeurs VIP (clients réguliers)',
        'Collaborations marques luxe (événements co-brandés)',
        'Stratégie UGC : inciter partages Instagram (malgré "No Photos" club)',
        'Monitoring competitors pour rester en avance de tendance'
      ],
      investment_needed: 'Pack Premium (2000€/mois) + Relations presse (1500€/mois)'
    },
    kpis: [
      { name: 'Articles presse/trimestre', current: '8', target: '12', gap: '+50%' },
      { name: 'Backlinks premium', current: '10', target: '25', gap: '+150%' },
      { name: 'Social mentions/mois', current: '120', target: '300', gap: '+150%' },
      { name: 'Top Lists inclusions', current: '4', target: '12', gap: '+200%' }
    ]
  },
  {
    id: 'nostalgia_content_strategy',
    name: 'Capitalisation Nostalgie Années 80 & Histoire Caroline Nin',
    objective: 'Exploiter storytelling années 80 pour différenciation émotionnelle',
    currentState: {
      score: 50,
      status: 'warning',
      analysis: '✅ STORYTELLING PUISSANT : Renaissance "adresse iconique Paris festif années 80". Caroline Nin (chanteuse jazz) y a débuté = maillage culturel riche. Pour moteur de recherche, cela valide authenticité : ce n\'est pas concept marketing ex nihilo, c\'est institution qui renaît. Termes "Orient-Express", "Art Déco", "Intimate atmosphere" enrichissent champ lexical. MAIS exploitation contenu SEO incomplète.',
      findings: [
        '✅ Storytelling années 80 présent dans presse',
        '✅ Mention Caroline Nin = ancrage culturel',
        '⚠️ Pas de page dédiée "Histoire Hollywood Savoy"',
        '❌ Vocabulaire années 80 sous-exploité en SEO',
        '✅ Décor Art Déco / Orient-Express bien mentionné',
        '⚠️ Opportunité contenu générationnel (Gen X, Millennials)'
      ]
    },
    opportunities: {
      potential: 'Nostalgie = connexion émotionnelle forte = fidélisation',
      estimatedImpact: 'Différenciation vs concurrents "nouveaux" sans histoire',
      timeline: '2-3 mois pour développer narrative complète'
    },
    recommendations: {
      quick_wins: [
        'Créer page "/histoire-hollywood-savoy-annees-80"',
        'Contenu "Caroline Nin et les débuts jazz du Savoy"',
        'Galerie photos archives années 80 (si disponibles)',
        'Timeline interactive : 1982 → 2025',
        'Blog posts "Renaissance d\'une légende parisienne"'
      ],
      strategic_actions: [
        'Interview Caroline Nin (si possible) pour contenu exclusif',
        'Collaboration artistes jazz actuels (événements)',
        'Partenariats médias culture/nostalgie (France Culture, Jazz Magazine)',
        'Soirées thématiques années 80 (générateur contenu)',
        'Book "Hollywood Savoy : De 1982 à 2025" (autorité ultime)'
      ],
      investment_needed: 'Pack Essentiel (1000€/mois) + production contenu (500€/mois)'
    },
    kpis: [
      { name: 'Trafic page histoire', current: '0', target: '8% total', gap: '+8%' },
      { name: 'Position "restaurant années 80"', current: '4', target: '1', gap: '-3' },
      { name: 'Temps sur page histoire', current: 'N/A', target: '4min+', gap: 'Création' },
      { name: 'Partages sociaux histoire', current: '0', target: '500+', gap: '+500' }
    ]
  }
];

// Résumé exécutif La Fontaine Gaillon
export const executiveSummaryFontaineGaillon = {
  overallAssessment: 'FONDATIONS SOLIDES MAIS INFRASTRUCTURE TECHNIQUE À MODERNISER',
  urgencyLevel: 'medium',
  estimatedLostRevenue: '120k€-180k€/an (GEO invisible + B2B sous-exploité)',
  quickWinsPotential: '+35% réservations en 4 mois',
  keyFindings: [
    '✅ E-réputation excellente (4.5 Google, 8.8 TheFork) - actif majeur',
    '✅ Terrasse néoclassique = différenciateur unique',
    '✅ 6 salons privés historiques = potentiel B2B inexploité',
    '❌ Site en sous-domaine fitz-group = dilution autorité',
    '❌ Menus PDF = invisible pour IA (GEO)',
    '⚠️ Transition identité Depardieu → Fitz en cours'
  ],
  recommendedPack: 'Premium',
  estimatedROI: '280-350% en 12 mois',
  timeline: '90 jours pour fondations + 6 mois consolidation'
};

// Résumé exécutif Hollywood Savoy
export const executiveSummaryHollywoodSavoy = {
  overallAssessment: 'HYPE FORT MAIS E-RÉPUTATION POLARISÉE À STABILISER D\'URGENCE',
  urgencyLevel: 'high',
  estimatedLostRevenue: '90k€-140k€/an (note Google 4.0 vs 4.5 potentiel)',
  quickWinsPotential: '+45% réservations en 3 mois',
  keyFindings: [
    '🚨 Polarisation e-réputation : 6.7 TheFork VS 5/5 Google',
    '⚠️ Note Google 4.0 = zone risque (seuil 4.5)',
    '✅ Couverture presse massive 2024-2025 (Vogue, Timeout)',
    '✅ Service exceptionnel 9.3/10 (Nissrine, Mouloud)',
    '❌ Identité hybride restaurant/club confuse pour algorithmes',
    '❌ Tomahawk & Caviar en PDF = invisibles IA'
  ],
  recommendedPack: 'Premium',
  estimatedROI: '320-420% en 12 mois',
  timeline: '60 jours actions critiques + 4 mois stabilisation'
};

export const getStrategicAnalysis = (restaurantId: string) => {
  if (restaurantId === '39v') {
    return strategicProjects39V;
  }
  if (restaurantId === 'cafe-max') {
    return strategicProjectsCafeMax;
  }
  if (restaurantId === 'fontaine-gaillon') {
    return strategicProjectsFontaineGaillon;
  }
  if (restaurantId === 'hollywood-savoy') {
    return strategicProjectsHollywoodSavoy;
  }
  if (restaurantId === 'le-president') {
    return strategicProjectsLePresident;
  }
  return [];
};

// Résumé exécutif Le 39V
export const executiveSummary39V = {
  overallAssessment: 'SITUATION CRITIQUE NÉCESSITANT ACTION IMMÉDIATE',
  urgencyLevel: 'high',
  estimatedLostRevenue: '150k€-250k€/an',
  quickWinsPotential: '+40% réservations en 3 mois',
  keyFindings: [
    '🚨 Score SEO 52/100 = pénalités Google actives',
    '🌍 7% seulement du trafic international (perte 80% potentiel)',
    '⭐ GMB sous-optimisé : 75% complétude, posts irréguliers',
    '🤖 Absent des recommandations IA (40% du trafic futur)',
    '🔧 18 erreurs SEO critiques bloquantes détectées',
    '📱 0 stratégie multi-canal réservation'
  ],
  recommendedPack: 'Premium',
  estimatedROI: '300-500% en 12 mois',
  timeline: '90 jours pour premiers résultats tangibles'
};

// Résumé exécutif Café Max
export const executiveSummaryCafeMax = {
  overallAssessment: 'INVISIBILITÉ DIGITALE + OPPORTUNITÉ HÔTELIÈRE MAJEURE NON EXPLOITÉE',
  urgencyLevel: 'critical',
  estimatedLostRevenue: '163k€/an (45k€ SEO + 118k€ hôtels)',
  quickWinsPotential: '+202% ROI digital + 118k€/an via hôtels',
  keyFindings: [
    '🚨 Position 48.59 = page 5 Google (invisible)',
    '🚨 Trafic 15/mois = -98% vs concurrent',
    '🚨 Score technique 45/100 = pénalités actives',
    '🏨 15 hôtels 4* (565 chambres) = 118k€/an NON CAPTÉ',
    '❌ 0 partenariat hôtel (concurrent en a 8+)',
    '❌ Histoire unique Jean Moulin non exploitée'
  ],
  recommendedPack: 'Premium',
  estimatedROI: '202% digital + 4650% hôtels',
  timeline: '90 jours pour sortir de la crise + captation hôtels'
};

// Analyse stratégique Le Président Lyon - ALARMISTE
export const strategicProjectsLePresident: StrategicProject[] = [
  {
    id: 'invisibilite_page_3',
    name: '🚨 CRISE: Invisibilité Totale - Position 24.5 (Page 3)',
    objective: 'Sortir d\'URGENCE de l\'invisibilité de la page 3 et atteindre la page 1 sur 30+ mots-clés stratégiques',
    currentState: {
      score: 12,
      status: 'critical',
      analysis: '🆘 SITUATION CATASTROPHIQUE : Position moyenne 24.5 = PAGE 3 Google. Statistiquement, MOINS DE 2% des internautes vont jusque-là. Vous perdez 95-98% du trafic potentiel. Sur 87 mots-clés détectés, AUCUN en page 1. Vos concurrents lyonnais (Têtedoie, Gourmet de Sèze) captent 100% du trafic pendant que vous êtes invisible.',
      findings: [
        '🚨 Position 24.5 = 95-98% du trafic perdu',
        '❌ 0 mot-clé en page 1 sur 87 trackés',
        '❌ "restaurant gastronomique lyon" (2400 rech/mois) : position 35+',
        '❌ "restaurant lyon 6" (3800 rech/mois) : position 30+',
        '❌ "restaurant grande bretagne lyon" (680 rech/mois) : position 25+',
        '❌ Concurrents captent 50-80 réservations/mois que vous manquez',
        '🆘 Perte estimée : 80-120k€/an à cause de ce seul problème'
      ]
    },
    opportunities: {
      potential: 'Passer 30 mots-clés en page 1 = +250-350% de trafic organique',
      estimatedImpact: '100-150 réservations/mois supplémentaires (80-120k€ CA/an)',
      timeline: '6-9 mois pour atteindre page 1 sur mots-clés principaux'
    },
    recommendations: {
      quick_wins: [
        'Optimiser 10 mots-clés "low-hanging fruits" (positions 11-20) → page 1 en 6-8 semaines',
        'Créer landing page "Restaurant Lyon 6ème" ultra-optimisée',
        'Corriger les 7 erreurs techniques critiques (robots.txt, sitemap, llms.txt)',
        'Obtenir 25 backlinks Lyon haute autorité (DA50+) en 3 mois'
      ],
      strategic_actions: [
        'Refonte sémantique complète du site (cluster topics)',
        'Campagne netlinking agressive : +350 backlinks en 12 mois',
        'Création 40 contenus optimisés (blog gastronomie lyonnaise)',
        'Partenariats médias locaux (Lyon Capitale, Le Progrès, etc.)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) MINIMUM pendant 12 mois'
    },
    kpis: [
      { name: 'Position moyenne', current: '24.5', target: '<10', gap: '-15 positions' },
      { name: 'Mots-clés page 1', current: '0', target: '30+', gap: '+30 mots-clés' },
      { name: 'Trafic organique', current: '3150/mois', target: '8000+/mois', gap: '+154%' },
      { name: 'Taux conversion', current: '2.5%', target: '4.5%', gap: '+2 points' }
    ]
  },
  {
    id: 'invisibilite_ia',
    name: '🤖 URGENCE 2026: Quasi-Invisibilité dans les IA (26/100)',
    objective: 'Devenir LA référence pour ChatGPT/Gemini sur "restaurant gastronomique Lyon 6" - Impact 40% des recherches 2026',
    currentState: {
      score: 26,
      status: 'critical',
      analysis: '🆘 RETARD MASSIF SUR LA RÉVOLUTION IA : Visibilité IA de 26/100 = vous êtes quasi-invisible pour ChatGPT et Gemini. SEULEMENT 4 pages citées sur tout votre site. 1030 audiences IA/mois alors que 8000+ est atteignable. En 2026, 40% des recherches passent par les IA. Vous êtes EN TRAIN DE MANQUER LE TRAIN de la plus grande révolution digitale de la décennie.',
      findings: [
        '🆘 AI Visibility : 26/100 (critique)',
        '❌ 4 pages citées vs 30-40 chez concurrents',
        '❌ Monthly AI Audience : 1030 (devrait être 8000+)',
        '❌ 43 mentions seulement (concurrents : 200+)',
        '❌ Llms.txt ABSENT = invisible pour les IA',
        '❌ 87K opportunités IA détectées = 0% captées',
        '🆘 Vous manquez 1000+ recommandations IA/mois = 40-60 réservations perdues'
      ]
    },
    opportunities: {
      potential: 'Multiplier par 5-8x votre visibilité IA en 6 mois',
      estimatedImpact: '60-100 réservations/mois via recommandations IA (50-80k€ CA/an)',
      timeline: '3-6 mois pour devenir autorité IA sur gastronomie Lyon'
    },
    recommendations: {
      quick_wins: [
        'Créer llms.txt optimisé IMMÉDIATEMENT (impact en 48h)',
        'Structurer 10 FAQ optimisées pour prompts IA',
        'Réécrire 4 pages existantes en format "IA-friendly"',
        'Implémenter Schema.org Restaurant complet'
      ],
      strategic_actions: [
        'Créer 30 contenus "prompt-optimized" (questions longues)',
        'Stratégie Featured Snippets (capter position 0)',
        'Optimisation pour recherche vocale (Siri, Alexa, Google Assistant)',
        'Veille active sur citations IA + renforcement'
      ],
      investment_needed: 'Pack Signature (3500€/mois) recommandé pour dominer segment IA'
    },
    kpis: [
      { name: 'AI Visibility', current: '26/100', target: '75+/100', gap: '+49 points' },
      { name: 'Pages citées', current: '4', target: '35+', gap: '+31 pages' },
      { name: 'AI Audience mensuel', current: '1030', target: '8000+', gap: '+677%' },
      { name: 'Mentions IA', current: '43', target: '250+', gap: '+207 mentions' }
    ]
  },
  {
    id: 'local_pack_hors_podium',
    name: '🗺️ DRAME LOCAL: Position 4 = Hors Podium Local Pack',
    objective: 'Intégrer le TOP 3 Local Pack Lyon 6ème - Seul le podium génère du trafic',
    currentState: {
      score: 38,
      status: 'critical',
      analysis: '💔 4ÈME = INVISIBLE : Vous êtes 4ème dans le Local Pack Lyon 6. Problème ? Google n\'affiche QUE le TOP 3. Position 4 = autant dire position 100. 80% des clics vont aux 3 premiers, vous récupérez les miettes. Vos 3 concurrents directs raflent 60-90 réservations/mois pendant que vous les regardez.',
      findings: [
        '💔 Position 4 Local Pack = HORS podium = invisible',
        '❌ GMB complété à 65% seulement (concurrents : 95%+)',
        '❌ 450 avis vs 800-1200 chez concurrents (signal faiblesse)',
        '❌ 43 citations vs 80-100 standard Lyon (crédibilité divisée par 2)',
        '❌ Posts GMB irréguliers (profil "inactif" pour Google)',
        '❌ Note 4.3 OK MAIS volume insuffisant (pas assez de social proof)',
        '🆘 Perte : 60-90 réservations/mois (48-72k€/an)'
      ]
    },
    opportunities: {
      potential: 'Intégrer TOP 3 Local Pack = +200-300% visibilité locale',
      estimatedImpact: '80-120 réservations/mois supplémentaires (65-95k€ CA/an)',
      timeline: '4-6 mois pour atteindre le podium'
    },
    recommendations: {
      quick_wins: [
        'Compléter GMB à 100% en 48h (FAQ, attributs, menus, services)',
        'Lancer posts GMB 3x/semaine (réactivation immédiate)',
        'Stratégie acquisition 300 avis en 6 mois (QR codes tables)',
        'Ajouter 50 citations locales en 3 mois'
      ],
      strategic_actions: [
        'Partenariats influenceurs Lyon (food bloggers)',
        'Programme fidélité avec incentive avis',
        'Optimisation profils TheFork + Michelin',
        'Photos professionnelles (100+ photos GMB)'
      ],
      investment_needed: 'Pack Premium (2000€/mois) incluant gestion GMB'
    },
    kpis: [
      { name: 'Position Local Pack', current: '4', target: 'Top 3', gap: '-1 position' },
      { name: 'Complétion GMB', current: '65%', target: '100%', gap: '+35%' },
      { name: 'Nombre d\'avis', current: '450', target: '750+', gap: '+300 avis' },
      { name: 'Citations', current: '43', target: '90+', gap: '+47 citations' }
    ]
  },
  {
    id: 'technique_fondations',
    name: '⚙️ FONDATIONS TECHNIQUES EN RUINE',
    objective: 'Corriger d\'URGENCE les 7 erreurs techniques critiques qui sabotent votre SEO',
    currentState: {
      score: 22,
      status: 'critical',
      analysis: '🏚️ FONDATIONS EFFONDRÉES : Votre infrastructure technique est en ruines. Sitemap.xml ABSENT, robots.txt INTROUVABLE, llms.txt INEXISTANT, Schema.org NON IMPLÉMENTÉ. C\'est comme avoir un restaurant 3 étoiles sans enseigne, sans menu et sans adresse. Google et les IA ne peuvent PAS vous indexer correctement. Vous perdez 60-70% de votre potentiel SEO à cause de ces bases non faites.',
      findings: [
        '🆘 Sitemap.xml ABSENT = 60-70% indexation perdue',
        '🆘 Robots.txt NON TROUVÉ = budget crawl gaspillé',
        '🆘 Llms.txt ABSENT = invisible pour IA',
        '❌ Schema.org manquant = 0 rich snippets',
        '❌ 3 pages non crawlées = contenu invisible',
        '❌ Ressources non cachées = site lent',
        '❌ Viewport sous-optimal = pénalité mobile',
        '🆘 Score technique estimé : 22/100 (désastreux)'
      ]
    },
    opportunities: {
      potential: 'Corriger ces 7 erreurs = +40-60% de trafic organique immédiat',
      estimatedImpact: '1200-1800 visites/mois supplémentaires (30-45k€ CA/an)',
      timeline: '2-4 semaines pour tout corriger'
    },
    recommendations: {
      quick_wins: [
        'Créer sitemap.xml + soumettre GSC (48h)',
        'Configurer robots.txt (2h)',
        'Créer llms.txt optimisé (4h)',
        'Implémenter Schema.org Restaurant (1 semaine)'
      ],
      strategic_actions: [
        'Audit technique complet (200 points)',
        'Optimisation performance (Core Web Vitals)',
        'Migration HTTP/2 + compression',
        'CDN + cache optimal'
      ],
      investment_needed: 'Inclus dans Pack Premium - Intervention technique immédiate'
    },
    kpis: [
      { name: 'Score technique', current: '22/100', target: '85+/100', gap: '+63 points' },
      { name: 'Pages indexées', current: '~15', target: '30+', gap: '+15 pages' },
      { name: 'Vitesse chargement', current: '4.2s', target: '<2s', gap: '-2.2s' },
      { name: 'Erreurs critiques', current: '7', target: '0', gap: '-7 erreurs' }
    ]
  },
  {
    id: 'contenu_branded',
    name: '📝 DÉPENDANCE BRANDED DANGEREUSE (70%)',
    objective: 'Inverser le ratio branded/non-branded : passer de 30/70 à 60/40',
    currentState: {
      score: 35,
      status: 'warning',
      analysis: '🔄 VOUS TOURNEZ EN ROND : 70% de votre trafic vient de recherches "branded" (gens qui vous connaissent déjà). Seulement 30% de nouvelles découvertes. Vous prêchez des convertis au lieu de conquérir. Résultat ? Croissance bloquée. Vos concurrents ont 60% non-branded, vous 30%. Vous stagnez pendant qu\'ils grossissent.',
      findings: [
        '⚠️ 70% branded = surplace (pas de croissance)',
        '❌ 30% non-branded = trop faible (doit être 60%+)',
        '❌ Invisible sur mots-clés commerciaux haute valeur',
        '❌ Pas de contenu "discovery" pour nouveaux clients',
        '❌ 0 blog / articles gastronomie lyonnaise',
        '❌ Pas de stratégie "top of funnel"',
        '🆘 Vous manquez 180-250 nouveaux clients/mois'
      ]
    },
    opportunities: {
      potential: 'Doubler le trafic non-branded = +2200 visites/mois nouvelles',
      estimatedImpact: '50-80 nouveaux clients/mois (40-65k€ CA/an)',
      timeline: '6-9 mois pour inverser le ratio'
    },
    recommendations: {
      quick_wins: [
        'Créer 10 pages commerciales (restaurant lyon 6, gastro lyon, etc.)',
        'Optimiser pour mots-clés découverte haute valeur',
        'Campagne Google Ads sur non-branded (boost immédiat)',
        'Partenariats blogs culinaires Lyon'
      ],
      strategic_actions: [
        'Blog gastronomie lyonnaise (50 articles/an)',
        'Guides culinaires (Où manger à Lyon, etc.)',
        'Stratégie contenu "top of funnel"',
        'SEO programmatique (100+ pages auto-générées)'
      ],
      investment_needed: 'Pack Premium avec volet Content Marketing'
    },
    kpis: [
      { name: 'Ratio non-branded', current: '30%', target: '60%', gap: '+30 points' },
      { name: 'Trafic nouveau', current: '945/mois', target: '4800/mois', gap: '+408%' },
      { name: 'Mots-clés commerciaux', current: '25', target: '120+', gap: '+95 KW' },
      { name: 'Contenus discovery', current: '0', target: '50+', gap: '+50 pages' }
    ]
  }
];

// Résumé exécutif Le Président Lyon
export const executiveSummaryLePresident = {
  overallAssessment: '🆘 INVISIBILITÉ DIGITALE CRITIQUE + RETARD MASSIF IA 2026',
  urgencyLevel: 'critical',
  estimatedLostRevenue: '180-240k€/an (SEO invisible + IA manquée + Local hors podium)',
  quickWinsPotential: '+250-350% trafic en 9 mois + captation révolution IA',
  keyFindings: [
    '🆘 Position 24.5 = page 3 Google = INVISIBLE (95% trafic perdu)',
    '🆘 AI Visibility 26/100 = quasi-absent révolution IA 2026',
    '💔 Position 4 Local Pack = HORS podium TOP 3 = invisible',
    '🏚️ 7 erreurs techniques critiques (sitemap, robots, llms.txt absents)',
    '🔄 70% branded = croissance bloquée, vous tournez en rond',
    '❌ 0 mot-clé en page 1 sur 87 trackés',
    '❌ 4 pages citées IA sur tout le site (doit être 35+)',
    '❌ 250 backlinks vs 600-800 concurrents (3x insuffisant)'
  ],
  recommendedPack: 'Premium → Signature après 3 mois',
  estimatedROI: '320-450% en 12 mois (hors révolution IA)',
  timeline: '3 mois quick wins + 9 mois transformation complète',
  criticalActions: [
    '48h : Créer sitemap.xml + robots.txt + llms.txt',
    '1 sem : Compléter GMB 100% + lancer posts 3x/semaine',
    '2 sem : 10 pages commerciales + 25 backlinks Lyon',
    '1 mois : Strategy IA + contenus prompt-optimized',
    '3 mois : 30 mots-clés page 1 + top 3 Local Pack',
    '6 mois : AI Visibility 75+ + domination segment Lyon 6',
    '12 mois : Leader digital gastronomie Lyon'
  ]
};
